import { useMemo } from 'react';
import { useActiveRole } from '@/hooks/useActiveRole';
import { UserRole } from '@features/authentication/types';
import type { InsightFilterOptions } from '@features/insight/hooks/useInsight';

interface DashboardInsightFilters {
  filters: InsightFilterOptions;
  queryEnabled: boolean;
  currentRole: UserRole | null;
}

/**
 * Builds insight filters from the active role context for the dashboard.
 *
 * Unlike InsightContext, this hook derives filters automatically
 * from the user's role context without any interactive state (no date range
 * picker, no tenant selector). Queries are disabled for SYSTEM_ADMIN
 * (needs tenant selection) and COURSE_REPRESENTATIVE (no insight access).
 */
export const useDashboardInsightFilters = (): DashboardInsightFilters => {
  const activeRole = useActiveRole();

  const filters = useMemo<InsightFilterOptions>(() => {
    const contextData = 'contextData' in activeRole ? activeRole.contextData : undefined;

    if (activeRole.currentRole === UserRole.UNIVERSITY_ADMIN) {
      return {
        ...(contextData?.tenantId && { tenantId: contextData.tenantId }),
      };
    }

    if (activeRole.currentRole === UserRole.FACULTY_ADMIN) {
      return {
        ...(contextData?.tenantId && { tenantId: contextData.tenantId }),
        ...(contextData?.domainId && { domainId: contextData.domainId }),
      };
    }

    if (activeRole.currentRole === UserRole.PROFESSOR) {
      return {
        ...(contextData?.tenantId && { tenantId: contextData.tenantId }),
        ...(contextData?.domainId && { domainId: contextData.domainId }),
        ...(contextData?.subjectId && { subjectId: contextData.subjectId }),
      };
    }

    return {};
  }, [activeRole]);

  const queryEnabled = useMemo(() => {
    if (activeRole.currentRole === UserRole.SYSTEM_ADMIN) return false;
    if (activeRole.currentRole === UserRole.COURSE_REPRESENTATIVE) return false;
    if (activeRole.currentRole === UserRole.UNIVERSITY_ADMIN) return !!filters.tenantId;
    if (activeRole.currentRole === UserRole.FACULTY_ADMIN)
      return !!filters.domainId && !!filters.tenantId;
    if (activeRole.currentRole === UserRole.PROFESSOR)
      return !!filters.subjectId && !!filters.tenantId;
    return false;
  }, [activeRole.currentRole, filters]);

  return {
    filters,
    queryEnabled,
    currentRole: activeRole.currentRole,
  };
};
